import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Autocomplete,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Computer as ComputerIcon,
  Search as SearchIcon,
  Rule as RuleIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  Webhook as WebhookIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { SeverityChip } from '../../../../../components/shared';
import {
  AlertsDrawerProps,
  AlertRule,
  AlertSubscriber,
  AlertSeverity,
  BackendAlertRule,
  getCardBorder,
  CARD_BORDER_RADIUS,
} from '../types';
import { notificationChannelApi } from '../../../../../api/cansense';
import type { ContactSearchResult } from '../../../../../api/cansense/notificationChannelApi';
import axios from '../../../../../api/axiosInstance';

// ----------------------------------------------------------------------------
// API helpers for endpoints without dedicated frontend service classes
// Routes: POST /sense/rules/:id/toggle, POST/DELETE /sense-exporters/exporters/:id/subscribe
// ----------------------------------------------------------------------------

const getExporterApiUrl = (endpoint: string): string =>
  process.env.NODE_ENV !== 'production'
    ? `http://localhost:10000/api3.2/sense-exporters${endpoint}`
    : `/api3.2/sense-exporters${endpoint}`;

const getRulesApiUrl = (endpoint: string): string =>
  process.env.NODE_ENV !== 'production'
    ? `http://localhost:10000/api3.2/sense/rules${endpoint}`
    : `/api3.2/sense/rules${endpoint}`;

const getForwardedHost = (apiUrl: string): string => {
  try {
    return apiUrl.startsWith('http')
      ? new URL(apiUrl).host
      : new URL(apiUrl, window.location.origin).host;
  } catch {
    return window.location.host;
  }
};

/**
 * Map a backend alert rule DB record to the frontend AlertRule interface.
 */
function mapBackendRule(rule: BackendAlertRule): AlertRule {
  // Build a human-readable threshold string from operator + value
  let threshold = '';
  if (rule.threshold_operator && rule.threshold_value !== null) {
    threshold = `${rule.threshold_operator} ${rule.threshold_value}`;
  } else if (rule.threshold_value !== null) {
    threshold = String(rule.threshold_value);
  }

  return {
    id: rule.id,
    name: rule.name,
    severity: (rule.severity || 'info') as AlertRule['severity'],
    threshold,
    duration: rule.for_duration || '',
    enabled: rule.enabled,
    isCustom: !rule.is_system,
  };
}

// Backend shape returned by GET /sense-exporters/exporters/:id/subscribers
interface BackendSubscriber {
  id: number;
  contact_id: number;
  severity_filter: string | null;
  contact_name: string;
  contact_email: string;
  has_slack: boolean;
  has_teams: boolean;
}

/**
 * Map a backend subscriber to the frontend AlertSubscriber interface.
 * Uses the subscription row ID (s.id) — this is what the DELETE endpoint expects.
 */
function mapBackendSubscriber(s: BackendSubscriber): AlertSubscriber {
  const type: AlertSubscriber['type'] = s.has_slack
    ? 'slack'
    : s.has_teams
      ? 'webhook'
      : 'email';

  // severity_filter is a comma-separated string like "critical,warning" or null (= all)
  const severities: AlertSeverity[] = s.severity_filter
    ? (s.severity_filter.split(',').map((v) => v.trim()) as AlertSeverity[])
    : ['critical', 'warning', 'info'];

  return {
    id: s.id,
    name: s.contact_name,
    type,
    value: s.contact_email || '',
    severities,
  };
}

/**
 * Deduplicate subscribers by contact_id, keeping the first occurrence.
 */
function deduplicateSubscribers(subs: BackendSubscriber[]): BackendSubscriber[] {
  const seen = new Set<number>();
  return subs.filter((s) => {
    if (seen.has(s.contact_id)) return false;
    seen.add(s.contact_id);
    return true;
  });
}

/**
 * AlertsDrawer - Side drawer for managing per-exporter alert rules and subscribers.
 *
 * Features:
 * - Header with exporter info and close button
 * - Active alerts banner (if any alerts are firing)
 * - Alert Rules section with:
 *   - Search and filter (All/Custom/Default)
 *   - Table showing rules with severity, status, and enable toggle
 *   - Visual distinction for custom vs default rules
 * - Subscribers section with:
 *   - List of email/webhook subscribers
 *   - Severity badges per subscriber
 *   - Add subscriber via contact search autocomplete
 *   - Remove subscriber via context menu with confirmation
 */
export function AlertsDrawer({
  open,
  onClose,
  exporter,
  alertRules: alertRulesProp,
  onCreateRule,
  onEditRule,
  onDeleteRule,
  onDuplicateRule,
  rulesVersion,
}: AlertsDrawerProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'custom' | 'default'>('all');

  // Fetched alert rules from backend
  const [fetchedRules, setFetchedRules] = useState<AlertRule[]>([]);
  const [fetchedBackendRules, setFetchedBackendRules] = useState<BackendAlertRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);

  // Fetched subscribers from backend
  const [subscribersLoading, setSubscribersLoading] = useState(false);

  // Use prop rules if explicitly provided, otherwise use fetched rules from backend
  const alertRules = alertRulesProp ?? fetchedRules;

  // Rule toggle state
  const [togglingRuleId, setTogglingRuleId] = useState<number | null>(null);

  // Add subscriber state
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [contactOptions, setContactOptions] = useState<ContactSearchResult[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [addingSubscriber, setAddingSubscriber] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Subscriber context menu state
  const [subscriberMenuAnchor, setSubscriberMenuAnchor] = useState<HTMLElement | null>(null);
  const [subscriberMenuTarget, setSubscriberMenuTarget] = useState<AlertSubscriber | null>(null);

  // Remove confirmation state
  const [removingTarget, setRemovingTarget] = useState<AlertSubscriber | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removingSubscriber, setRemovingSubscriber] = useState(false);

  // Local subscribers for optimistic add/remove
  const [localSubscribers, setLocalSubscribers] = useState<AlertSubscriber[]>([]);

  // Snackbar feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  // Fetch subscribers from backend when drawer opens
  useEffect(() => {
    if (!open || !exporter) return;

    let cancelled = false;
    const fetchSubscribers = async () => {
      setSubscribersLoading(true);
      try {
        const url = getExporterApiUrl(`/exporters/${exporter.id}/subscribers`);
        const response = await axios.get(url, {
          headers: { 'X-Forwarded-Host': getForwardedHost(url) },
        });
        if (!cancelled && response.data?.success && response.data.data?.subscribers) {
          setLocalSubscribers(
            deduplicateSubscribers(response.data.data.subscribers).map(mapBackendSubscriber)
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[AlertsDrawer] Failed to fetch subscribers:', err);
        }
      } finally {
        if (!cancelled) setSubscribersLoading(false);
      }
    };
    fetchSubscribers();
    return () => { cancelled = true; };
  }, [open, exporter]);

  // Fetch alert rules from backend when drawer opens (unless prop rules were provided)
  useEffect(() => {
    if (!open || !exporter || alertRulesProp) return;

    let cancelled = false;
    const fetchRules = async () => {
      setRulesLoading(true);
      try {
        const url = getRulesApiUrl('');
        const response = await axios.get(url, {
          headers: { 'X-Forwarded-Host': getForwardedHost(url) },
        });
        if (!cancelled && response.data?.success && response.data.data?.rules) {
          const backendRules: BackendAlertRule[] = response.data.data.rules;
          setFetchedBackendRules(backendRules);
          setFetchedRules(backendRules.map(mapBackendRule));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[AlertsDrawer] Failed to fetch alert rules:', err);
        }
      } finally {
        if (!cancelled) setRulesLoading(false);
      }
    };
    fetchRules();
    return () => { cancelled = true; };
  }, [open, exporter, alertRulesProp, rulesVersion]);

  // Cleanup search debounce timer
  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  // Reset transient state when drawer closes
  useEffect(() => {
    if (!open) {
      setShowAddSubscriber(false);
      setContactOptions([]);
      setSubscriberMenuAnchor(null);
      setSubscriberMenuTarget(null);
      setConfirmRemoveOpen(false);
      setRemovingTarget(null);
    }
  }, [open]);

  // Filter rules based on search and filter status
  const filteredRules = useMemo(() => {
    return alertRules.filter((rule) => {
      const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter =
        filterStatus === 'all' ||
        (filterStatus === 'custom' && rule.isCustom) ||
        (filterStatus === 'default' && !rule.isCustom);
      return matchesSearch && matchesFilter;
    });
  }, [alertRules, searchQuery, filterStatus]);

  // ---- Rule toggle handler ----
  // Uses optimistic update on fetchedRules so the toggle state persists across
  // drawer close/reopen (since fetchedRules is refreshed from the DB each time
  // the drawer opens, and the backend toggle writes to the DB).
  const handleToggleRule = async (rule: AlertRule) => {
    const newEnabled = !rule.enabled;
    setTogglingRuleId(rule.id);

    // Optimistic update
    const updateRuleEnabled = (enabled: boolean) =>
      setFetchedRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, enabled } : r))
      );
    updateRuleEnabled(newEnabled);

    try {
      const url = getRulesApiUrl(`/${rule.id}/toggle`);
      const response = await axios.post(
        url,
        {},
        { headers: { 'X-Forwarded-Host': getForwardedHost(url) } }
      );
      // Use the authoritative enabled state from the backend response
      if (response.data?.success && response.data.data?.enabled !== undefined) {
        updateRuleEnabled(response.data.data.enabled);
      }
      showSnackbar(`${rule.name} ${newEnabled ? 'enabled' : 'disabled'}`, 'success');
    } catch {
      // Revert optimistic update on failure
      updateRuleEnabled(!newEnabled);
      showSnackbar(`Failed to toggle ${rule.name}`, 'error');
    } finally {
      setTogglingRuleId(null);
    }
  };

  // ---- Contact search (debounced 300ms) ----
  const handleContactSearch = (_event: React.SyntheticEvent, value: string) => {
    clearTimeout(searchTimerRef.current);
    if (value.length < 2) {
      setContactOptions([]);
      setContactLoading(false);
      return;
    }
    setContactLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await notificationChannelApi.searchContacts(value);
        setContactOptions(results);
      } catch {
        setContactOptions([]);
      } finally {
        setContactLoading(false);
      }
    }, 300);
  };

  // ---- Add subscriber on autocomplete selection ----
  const handleAddSubscriberSelect = async (
    _event: React.SyntheticEvent,
    contact: ContactSearchResult | null
  ) => {
    if (!contact || !exporter) return;
    setAddingSubscriber(true);
    try {
      const url = getExporterApiUrl(`/exporters/${exporter.id}/subscribe`);
      const response = await axios.post(
        url,
        { contactId: contact.contactId, severities: ['critical', 'warning'] },
        { headers: { 'X-Forwarded-Host': getForwardedHost(url) } }
      );
      // Use the subscription row ID from the backend response for delete compatibility
      const subscriptionId = response.data?.data?.subscription?.id ?? contact.contactId;
      const newSub: AlertSubscriber = {
        id: subscriptionId,
        name: `${contact.firstName} ${contact.lastName}`,
        type: 'email',
        value: contact.email,
        severities: ['critical', 'warning'] as AlertSeverity[],
      };
      setLocalSubscribers((prev) => [...prev, newSub]);
      setShowAddSubscriber(false);
      setContactOptions([]);
      showSnackbar(`${contact.firstName} ${contact.lastName} subscribed`, 'success');
    } catch {
      showSnackbar('Failed to add subscriber', 'error');
    } finally {
      setAddingSubscriber(false);
    }
  };

  // ---- Subscriber context menu handlers ----
  const handleSubscriberMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    sub: AlertSubscriber
  ) => {
    event.stopPropagation();
    setSubscriberMenuAnchor(event.currentTarget);
    setSubscriberMenuTarget(sub);
  };

  const handleSubscriberMenuClose = () => {
    setSubscriberMenuAnchor(null);
    setSubscriberMenuTarget(null);
  };

  const handleRemoveClick = () => {
    setRemovingTarget(subscriberMenuTarget);
    setSubscriberMenuAnchor(null);
    setSubscriberMenuTarget(null);
    setConfirmRemoveOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!removingTarget || !exporter) return;
    setRemovingSubscriber(true);
    try {
      const url = getExporterApiUrl(
        `/exporters/${exporter.id}/subscribe/${removingTarget.id}`
      );
      await axios.delete(url, {
        headers: { 'X-Forwarded-Host': getForwardedHost(url) },
      });
      setLocalSubscribers((prev) => prev.filter((s) => s.id !== removingTarget.id));
      showSnackbar(`${removingTarget.name} removed`, 'success');
    } catch {
      showSnackbar(`Failed to remove ${removingTarget.name}`, 'error');
    } finally {
      setRemovingSubscriber(false);
      setConfirmRemoveOpen(false);
      setRemovingTarget(null);
    }
  };

  // Don't render if no exporter selected (after all hooks)
  if (!exporter) return null;

  const customCount = alertRules.filter((r) => r.isCustom).length;
  const hasActiveAlerts = exporter.status === 'down' || exporter.status === 'warning';
  const activeAlertCount = exporter.status === 'down' ? 2 : exporter.status === 'warning' ? 1 : 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500, md: 550 } },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: getCardBorder(theme),
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
            <ComputerIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {exporter.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {exporter.name} &bull; {exporter.type}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Active Alerts Banner */}
      {hasActiveAlerts && (
        <Box
          sx={{
            mx: 2,
            mt: 2,
            p: 1.5,
            bgcolor: 'error.main',
            borderRadius: CARD_BORDER_RADIUS,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ErrorIcon sx={{ color: 'white' }} />
          <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>
            {activeAlertCount} active alert{activeAlertCount > 1 ? 's' : ''} firing
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              const rulesSection = document.getElementById('alerts-drawer-rules');
              rulesSection?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{
              bgcolor: 'white',
              color: 'error.main',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            View
          </Button>
        </Box>
      )}

      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        {/* Alert Rules Section */}
        <Paper
          id="alerts-drawer-rules"
          elevation={0}
          sx={{
            border: getCardBorder(theme),
            borderRadius: CARD_BORDER_RADIUS,
            mb: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: getCardBorder(theme),
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <RuleIcon color="primary" />
              <Typography fontWeight={600}>Alert Rules</Typography>
              <Chip
                label={`${customCount} custom`}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
              />
              <Box sx={{ flex: 1 }} />
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="contained"
                onClick={() => onCreateRule?.()}
              >
                Create Rule
              </Button>
            </Box>

            {/* Search and Filter */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search rules..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, minWidth: 150 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip
                  label="All"
                  size="small"
                  variant={filterStatus === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setFilterStatus('all')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={`Custom (${customCount})`}
                  size="small"
                  color="primary"
                  variant={filterStatus === 'custom' ? 'filled' : 'outlined'}
                  onClick={() => setFilterStatus('custom')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Default"
                  size="small"
                  variant={filterStatus === 'default' ? 'filled' : 'outlined'}
                  onClick={() => setFilterStatus('default')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          </Box>

          {/* Rules Table */}
          {rulesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rule</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell padding="none" sx={{ width: 72 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRules.map((rule) => (
                  <TableRow
                    key={rule.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: rule.isCustom
                        ? alpha(theme.palette.primary.main, 0.03)
                        : 'inherit',
                      opacity: rule.enabled ? 1 : 0.5,
                    }}
                    onClick={() => {
                      const backendRule = fetchedBackendRules.find((r) => r.id === rule.id);
                      if (backendRule) onEditRule?.(backendRule);
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={rule.isCustom ? 600 : 400}
                      >
                        {rule.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontFamily="monospace"
                      >
                        {rule.threshold}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SeverityChip severity={rule.severity} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.isCustom ? 'Custom' : 'Default'}
                        size="small"
                        color={rule.isCustom ? 'primary' : 'default'}
                        variant={rule.isCustom ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={rule.enabled}
                        size="small"
                        onChange={() => handleToggleRule(rule)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={togglingRuleId === rule.id}
                      />
                    </TableCell>
                    <TableCell align="right" padding="none" sx={{ width: 72, whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        title="Duplicate rule"
                        onClick={(e) => {
                          e.stopPropagation();
                          const br = fetchedBackendRules.find((r) => r.id === rule.id);
                          if (br) onDuplicateRule?.(br);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      {rule.isCustom ? (
                        <IconButton
                          size="small"
                          title="Delete rule"
                          onClick={(e) => {
                            e.stopPropagation();
                            const br = fetchedBackendRules.find((r) => r.id === rule.id);
                            if (br) onDeleteRule?.(br);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Tooltip title="Default rules cannot be deleted" placement="left">
                          <span>
                            <IconButton size="small" disabled onClick={(e) => e.stopPropagation()}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>

        {/* Subscribers Section */}
        <Paper
          elevation={0}
          sx={{
            border: getCardBorder(theme),
            borderRadius: CARD_BORDER_RADIUS,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: getCardBorder(theme),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              <Typography fontWeight={600}>
                Subscribers ({localSubscribers.length})
              </Typography>
            </Box>
            <Button
              startIcon={showAddSubscriber ? <CloseIcon /> : <AddIcon />}
              size="small"
              variant={showAddSubscriber ? 'outlined' : 'contained'}
              onClick={() => {
                setShowAddSubscriber((prev) => !prev);
                setContactOptions([]);
              }}
            >
              {showAddSubscriber ? 'Cancel' : 'Add'}
            </Button>
          </Box>

          {/* Inline contact search autocomplete */}
          {showAddSubscriber && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: getCardBorder(theme) }}>
              <Autocomplete<ContactSearchResult, false, false, false>
                value={null}
                options={contactOptions}
                loading={contactLoading}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName}`
                }
                isOptionEqualToValue={(option, value) =>
                  option.contactId === value.contactId
                }
                filterOptions={(x) => x}
                onInputChange={handleContactSearch}
                onChange={handleAddSubscriberSelect}
                noOptionsText="Type 2+ characters to search"
                loadingText="Searching..."
                disabled={addingSubscriber}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search contacts by name or email..."
                    size="small"
                    autoFocus
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {(contactLoading || addingSubscriber) && (
                            <CircularProgress size={16} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.contactId}>
                    <Box>
                      <Typography variant="body2">
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          )}

          {subscribersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
          <Box sx={{ p: 2 }}>
            {localSubscribers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No subscribers yet
              </Typography>
            )}
            {localSubscribers.map((sub) => (
              <Paper
                key={sub.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1,
                  border: getCardBorder(theme),
                  borderRadius: CARD_BORDER_RADIUS,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                  }}
                >
                  {sub.type === 'email' ? (
                    <EmailIcon fontSize="small" />
                  ) : (
                    <WebhookIcon fontSize="small" />
                  )}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {sub.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sub.value}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {sub.severities.map((s) => (
                    <SeverityChip key={s} severity={s} />
                  ))}
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleSubscriberMenuOpen(e, sub)}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Box>
          )}
        </Paper>
      </Box>

      {/* Subscriber context menu */}
      <Menu
        anchorEl={subscriberMenuAnchor}
        open={Boolean(subscriberMenuAnchor)}
        onClose={handleSubscriberMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 160,
            borderRadius: CARD_BORDER_RADIUS,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
        }}
      >
        <MenuItem onClick={handleRemoveClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>

      {/* Remove subscriber confirmation dialog */}
      <Dialog
        open={confirmRemoveOpen}
        onClose={removingSubscriber ? undefined : () => setConfirmRemoveOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Subscriber</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{removingTarget?.name}</strong> from{' '}
            {exporter.displayName} alerts?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmRemoveOpen(false)}
            disabled={removingSubscriber}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            color="error"
            disabled={removingSubscriber}
            startIcon={
              removingSubscriber ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {removingSubscriber ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
}

export default AlertsDrawer;
